import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { MaterialReactTable } from "material-react-table";
import { mkConfig, generateCsv, download } from "export-to-csv";
import { useDispatch, useSelector } from "react-redux";
import { hasPermission } from "../Utils/UtilityFunctions";
import { useColumnSettings } from "../Utils/useColumnSettings";
import { Loader } from "../Utils/Loader";
import CommonModal from "../Utils/CommonModal";
import PriceBookForm from "./PriceBookForm";
import { getPriceBook } from "../../slices/PriceBook/GetPriceBook/thunk";
import { MenuItem } from "@mui/material";
import { deletePriceBook } from "../../slices/PriceBook/DeletePriceBook/thunk";
import { formatCurrency } from "../Utils/Currency";

function PriceBookListings() {
  const dispatch = useDispatch();

  const rawPermissions = useSelector(
    (state) => state.permissions.permissionsMap,
  );

  const permissionsMap = new Map(
    rawPermissions.map(([key, value]) => [key, new Set(value)]),
  );

  const pricebookPermission = hasPermission(
    permissionsMap,
    "configuration",
    "pricebook",
  );

  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [allItems, setAllItems] = useState([]);

  const [selectedItem, setSelectedItem] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteItemData, setDeleteItemData] = useState(null);
  const [messageForPopUp, setMessageForPopUp] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    document.body.style.overflow = "auto";
  };

  const handleDelete = (item) => {
    setDeleteItemData(item);

    setMessageForPopUp(
      `Are you sure you want to delete item "${item?.item}" ?`,
    );

    toggleDeleteModal();
  };

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { priceBooks, loading } = useSelector(
    (state) => state.GetPriceBook || {},
  );

  useEffect(() => {
    dispatch(getPriceBook(pagination.pageSize, pagination.pageIndex + 1));
  }, [dispatch, pagination.pageSize, pagination.pageIndex]);

  useEffect(() => {
    if (priceBooks?.result?.length) {
      const formattedData = priceBooks.result.map((item) => ({
        itemid: item?.itemid,
        sku: item?.sku || "-",
        item: item?.itemname || "-",
        category: item?.category || "-",
        listprice: item?.listprice || 0,
        cost: item?.unitprice || 0,
      }));

      setAllItems((prev) => {
        const merged = [...prev, ...formattedData];

        return merged.filter(
          (item, index, self) =>
            index === self.findIndex((x) => x.itemid === item.itemid),
        );
      });

      setFilteredData(formattedData);
    } else {
      setFilteredData([]);
    }
  }, [priceBooks]);

  // SEARCH
  const handleSearch = () => {
    if (!searchText.trim()) {
      if (priceBooks?.result?.length) {
        const resetData = priceBooks.result.map((item) => ({
          itemid: item?.itemid,
          sku: item?.sku || "-",
          item: item?.itemname || "-",
          category: item?.category || "-",
          listprice: item?.listprice || 0,
          cost: item?.unitprice || 0,
        }));

        setFilteredData(resetData);
      }

      return;
    }

    const search = searchText.toLowerCase();

    const filtered = allItems.filter((item) => {
      return (
        item?.item?.toLowerCase()?.includes(search) ||
        item?.sku?.toLowerCase()?.includes(search) ||
        item?.category?.toLowerCase()?.includes(search) ||
        String(item?.cost)?.toLowerCase()?.includes(search)
      );
    });

    setFilteredData(filtered);
  };

  // CSV EXPORT
  const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
  });

  const handleExportData = () => {
    const csv = generateCsv(csvConfig)(filteredData);

    download(csvConfig)(csv);
  };

  // TABLE COLUMNS
  const columns = useMemo(
    () => [
      {
        accessorKey: "sku",
        header: "SKU",
      },
      {
        accessorKey: "item",
        header: "Product",
      },
      {
        accessorKey: "category",
        header: "Category",
      },
      //   {
      //     accessorKey: "listprice",
      //     header: "List Price",
      //     Cell: ({ cell }) => `₹${Number(cell.getValue()).toLocaleString()}`,
      //   },
      {
        accessorKey: "cost",
        header: "Unit Price",
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
    ],
    [],
  );

  const { toggleSettingsModal, CustomViewModal } = useColumnSettings(
    "pricebook",
    columns,
  );

  const itemOptions =
    priceBooks?.result?.map((item) => ({
      label: item?.itemname,
      value: item?.itemid,
    })) || [];

  return (
    <React.Fragment>
      <div id="csms">
        <Container fluid>
          <Row>
            {/* PAGE HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="rv-page-title m-0">Price List</h5>
            </div>

            {/* SEARCH + ACTIONS */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
              <div className="d-flex align-items-center w-100 gap-2">
                <Input
                  type="text"
                  placeholder="Type to search here"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  style={{
                    maxWidth: "500px",
                    height: "36px",
                    borderRadius: "4px",
                  }}
                />

                <Button
                  className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap mx-1"
                  onClick={handleSearch}
                >
                  <i className="ri-search-line label-icon align-middle fs-16 me-1 fw-bold"></i>

                  <span className="ps-2 ms-2 me-1 fw-bold">Search</span>
                </Button>
              </div>

              <div className="d-flex align-items-center">
                {pricebookPermission?.has("export") && (
                  <Button
                    className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap mx-1"
                    onClick={handleExportData}
                  >
                    <i className="ri-download-line label-icon secondary-label-icon align-middle fs-16 me-2 fw-bold"></i>

                    <span className="px-2 ps-3 ms-2 me-1 fw-bold">Export</span>
                  </Button>
                )}

                {pricebookPermission?.has("create") && (
                  <Button
                    className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap mx-1"
                    onClick={() => {
                      setSelectedItem(null);
                      toggleModal();
                    }}
                  >
                    <i className="ri-add-fill label-icon align-middle fs-16 me-1 fw-bold"></i>

                    <span className="ps-2 ms-2 me-1 fw-bold">
                      Add Price Book
                    </span>
                  </Button>
                )}

                {/* {pricebookPermission?.has("view") && (
                  <div
                    className="btn-icon cursor-img"
                    onClick={toggleSettingsModal}
                  >
                    <i className="ri-settings-2-line fs-4"></i>
                  </div>
                )} */}
              </div>
            </div>

            {/* TABLE */}
            <Col lg={12}>
              <div
                className="border table-responsive"
                style={{ borderRadius: "4px" }}
              >
                {loading && <Loader />}

                <MaterialReactTable
                  columns={columns}
                  data={filteredData}
                  enableRowActions
                  enableColumnOrdering={false}
                  enableColumnDragging={false}
                  enableDensityToggle={false}
                  enableColumnActions={false}
                  enableTopToolbar={false}
                  manualPagination
                  rowCount={priceBooks?.totalcount || 0}
                  onPaginationChange={setPagination}
                  state={{
                    pagination,
                  }}
                  displayColumnDefOptions={{
                    "mrt-row-actions": {
                      header: "",
                      size: 20,
                      minSize: 20,
                      maxSize: 20,
                    },
                  }}
                  muiTableBodyCellProps={({ column }) => ({
                    sx:
                      column.id === "mrt-row-actions"
                        ? {
                            padding: "0 0px",
                          }
                        : {},
                  })}
                  renderRowActionMenuItems={({ row, closeMenu }) =>
                    [
                      pricebookPermission?.has("update") && (
                        <MenuItem
                          key="edit"
                          onClick={() => {
                            closeMenu();
                            setSelectedItem(row.original);
                            setModalOpen(true);
                          }}
                        >
                          <i className="bx bxs-edit me-2" />
                          Edit
                        </MenuItem>
                      ),

                      pricebookPermission?.has("delete") && (
                        <MenuItem
                          key="delete"
                          onClick={() => {
                            closeMenu();
                            handleDelete(row.original);
                          }}
                        >
                          <i className="ri ri-delete-bin-5-line me-2" />
                          Delete
                        </MenuItem>
                      ),
                    ].filter(Boolean)
                  }
                />
              </div>
            </Col>

            {CustomViewModal()}
          </Row>
        </Container>
      </div>

      <CommonModal
        modalHeader="Delete Price Book"
        open={deleteModal}
        toggle={toggleDeleteModal}
        buttonText="Yes, Delete It"
        closeButtonText="Cancel"
        messageForPopUp={messageForPopUp}
        modalAction={async () => {
          await dispatch(
            deletePriceBook(deleteItemData?.itemid, setDeleteLoading),
          );
          await dispatch(
            getPriceBook(pagination.pageSize, pagination.pageIndex + 1),
          );
          toggleDeleteModal();
        }}
      />

      <Modal isOpen={modalOpen} toggle={toggleModal} centered size="md">
        <ModalHeader
          toggle={toggleModal}
          style={{
            borderBottom: "1px solid #FD7545",
            padding: "16px 20px",
          }}
        >
          <span
            style={{
              color: "#FD7545",
              fontWeight: "600",
            }}
          >
            {selectedItem ? "Edit Price" : "Add Price"}
          </span>
        </ModalHeader>

        <ModalBody
          className="overflow-auto"
          style={{ maxHeight: "70vh", padding: "20px 20px 0px 20px" }}
        >
          <PriceBookForm
            closeModal={toggleModal}
            onSuccess={() => {
              dispatch(
                getPriceBook(pagination.pageSize, pagination.pageIndex + 1),
              );
            }}
            selectedItem={selectedItem}
            itemOptions={itemOptions}
          />
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
}

export default PriceBookListings;
